/**
 * Preset list, save/load/share handlers for FilterBarWithPresets.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useCallback, useMemo, useState } from 'react';

import { isStatsSavedFilterRollingPreset } from '@trafficpulse/shared';

import type { SavedFilter } from '@/features/smswiser-campaigns/types/saved-filter.types';
import {
  useSavedFilters,
  useSaveFilter,
  useUpdateFilter,
  useDeleteFilter,
  useShareFilter,
  useUnshareFilter,
} from '@/features/smswiser-campaigns/hooks/useSavedFilters';
import { dmyToIso, isoToDmy, todayIsoDateParis } from '@/features/smswiser-campaigns/utils/dateFormat';

import {
  getRangeFromPreset,
  type StatsDateRangePreset,
} from '@/components/date-range';
import type { SaveFilterSubmitData } from '../modals';

import type { UseFilterBarWithPresetsCoreArgs } from './filterBarWithPresetsCore.types';
import { persistSavedStatsFilter } from './persistSavedStatsFilter';

export function useFilterBarWithPresetsCore({
  tab,
  fromDate,
  toDate,
  onDateRangeChange,
  filtersState,
  setFilters,
}: UseFilterBarWithPresetsCoreArgs) {
  const { data: presets = [] } = useSavedFilters(tab);
  const saveFilter = useSaveFilter();
  const updateFilter = useUpdateFilter();
  const deleteFilter = useDeleteFilter();
  const shareFilter = useShareFilter();
  const unshareFilter = useUnshareFilter();

  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<SavedFilter | null>(null);
  const [sharingPreset, setSharingPreset] = useState<SavedFilter | null>(null);

  const currentDateRangeDmy = useMemo(
    () => ({ from: isoToDmy(fromDate), to: isoToDmy(toDate) }),
    [fromDate, toDate],
  );

  const handleLoadPreset = useCallback(
    (preset: SavedFilter) => {
      setActivePresetId(preset.id);
      const filters = preset.filters.map((f) => ({ field: f.field, operator: f.operator, value: f.value }));
      setFilters(() => ({ filters, active: true }));
      if (!onDateRangeChange) return;
      if (
        preset.dateRangePreset &&
        isStatsSavedFilterRollingPreset(preset.dateRangePreset)
      ) {
        const range = getRangeFromPreset(preset.dateRangePreset as StatsDateRangePreset);
        if (range) {
          onDateRangeChange(range.from, range.to);
        }
        return;
      }
      if (!preset.dateFrom) return;
      const fi = dmyToIso(preset.dateFrom);
      if (!fi) return;
      const ti = preset.dateToUntilToday
        ? todayIsoDateParis()
        : preset.dateTo
          ? dmyToIso(preset.dateTo)
          : null;
      if (!ti) return;
      onDateRangeChange(fi, ti);
    },
    [setFilters, onDateRangeChange],
  );

  const handleSave = useCallback(
    async (data: SaveFilterSubmitData) => {
      await persistSavedStatsFilter(
        data,
        filtersState.filters,
        editingPreset,
        saveFilter,
        updateFilter,
        shareFilter,
      );
      setEditingPreset(null);
    },
    [filtersState.filters, editingPreset, saveFilter, updateFilter, shareFilter],
  );

  const handleEdit = useCallback((preset: SavedFilter) => {
    setEditingPreset(preset);
    setSaveModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteFilter.mutateAsync(id);
      if (activePresetId === id) setActivePresetId(null);
    },
    [deleteFilter, activePresetId],
  );

  const handleShareOpen = useCallback((preset: SavedFilter) => {
    setSharingPreset(preset);
    setShareModalOpen(true);
  }, []);

  const editingInitial = useMemo(() => {
    if (!editingPreset) return null;
    return {
      name: editingPreset.name,
      color: editingPreset.color,
      tabs: editingPreset.tabs,
      dateFrom: editingPreset.dateFrom,
      dateTo: editingPreset.dateTo,
      dateToUntilToday: editingPreset.dateToUntilToday,
      dateRangePreset: editingPreset.dateRangePreset ?? null,
    };
  }, [editingPreset]);

  const onShareUsers = useCallback(
    async (userIds: string[]) => {
      if (!sharingPreset) return;
      await shareFilter.mutateAsync({ filterId: sharingPreset.id, userIds });
    },
    [sharingPreset, shareFilter],
  );

  const onUnshareUser = useCallback(
    async (userId: string) => {
      if (!sharingPreset) return;
      await unshareFilter.mutateAsync({ filterId: sharingPreset.id, userId });
    },
    [sharingPreset, unshareFilter],
  );

  return {
    presets,
    activePresetId,
    saveModalOpen,
    setSaveModalOpen,
    shareModalOpen,
    setShareModalOpen,
    setEditingPreset,
    sharingPreset,
    currentDateRangeDmy,
    editingInitial,
    handleLoadPreset,
    handleSave,
    handleEdit,
    handleDelete,
    handleShareOpen,
    onShareUsers,
    onUnshareUser,
    saveSubmitting: saveFilter.isPending || updateFilter.isPending,
    shareSubmitting: shareFilter.isPending || unshareFilter.isPending,
  };
}
